import { ShieldCheck } from "lucide-react";
import { textoContatoIgreja, type ContatoIgreja } from "@/core/utils/contatoIgreja";

/**
 * Aviso de privacidade + confirmação única (decisão de 09/10/2026, VIS-DEC-032).
 * O voluntário lê/mostra o aviso e marca que a pessoa foi avisada e concordou; o banco
 * registra quando e quem. Base: LGPD art. 8 (prova do consentimento é da igreja) e art. 14
 * (criança: consentimento do responsável).
 * Pendência técnica (VIS-PEN-02): o prazo para apagar os dados ainda não foi definido pela igreja.
 */

export type VarianteAviso = "pessoa" | "responsavel" | "familia";

interface Props {
  variante: VarianteAviso;
  contato: Partial<ContatoIgreja> | null | undefined;
  marcado: boolean;
  onMarcar: (valor: boolean) => void;
  erro?: string;
  /** Pessoa de 12 a 17 anos: lembra de avisar também o responsável. */
  adolescente?: boolean;
  /** Família: há criança no grupo? (acrescenta a frase sobre o que se guarda delas.) */
  comCrianca?: boolean;
  desabilitado?: boolean;
}

const ROTULO_CHECK: Record<VarianteAviso, string> = {
  pessoa: "Avisei a pessoa e ela concordou.",
  responsavel: "Avisei o responsável e ele concordou.",
  familia: "Avisei os adultos e eles concordaram.",
};

export function AvisoConsentimento({
  variante,
  contato,
  marcado,
  onMarcar,
  erro,
  adolescente = false,
  comCrianca = true,
  desabilitado = false,
}: Props) {
  const via = textoContatoIgreja(contato);

  return (
    <section
      aria-label="Aviso de privacidade"
      className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4"
    >
      <p className="flex items-start gap-2 text-sm leading-relaxed text-text">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        <span>
          {variante === "pessoa" && (
            <>
              <strong>Avise a pessoa:</strong> a igreja guarda nome, contato e o que ela responder (
              <strong>inclusive sobre religião e oração</strong>) só para cuidar dela. Só a
              liderança vê. Ela pode pedir para apagar quando quiser, {via}.
            </>
          )}
          {variante === "responsavel" && (
            <>
              <strong>Avise o pai, a mãe ou o responsável:</strong> a igreja guarda o nome e a idade
              da criança e o contato do responsável, só para cuidar da família. Só a liderança vê.
              Ele pode pedir para apagar quando quiser, {via}.
            </>
          )}
          {variante === "familia" && (
            <>
              <strong>Avise os adultos:</strong> a igreja guarda nome, contato e o que responderem (
              <strong>inclusive sobre religião e oração</strong>) só para cuidar da família.{" "}
              {comCrianca && "Das crianças guardamos só nome, idade e o contato do responsável. "}
              Só a liderança vê. Podem pedir para apagar quando quiserem, {via}.
            </>
          )}
        </span>
      </p>

      {adolescente && variante === "pessoa" && (
        <p className="text-xs font-medium text-warning">
          Menor de 18 anos: avise também o pai, a mãe ou o responsável.
        </p>
      )}

      <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-semibold text-text">
        <input
          type="checkbox"
          checked={marcado}
          disabled={desabilitado}
          onChange={(e) => onMarcar(e.target.checked)}
          className="h-5 w-5 shrink-0 accent-primary"
        />
        {ROTULO_CHECK[variante]}
      </label>
      {erro && (
        <span role="alert" className="text-xs text-danger">
          {erro}
        </span>
      )}
    </section>
  );
}
